import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import {
    INIT_VOUCHER_REFERENCE,
    SET_VOUCHER_REFERENCE,
    SetVoucherReference,
    Validate,
    VALIDATE,
    ValidateFail,
    ValidateSuccess
} from './order-checkout.actions';
import { HeadersEnum, ParamsKeys, ParamsValues, RestURLsEnum } from '../../../model/http-request-enums';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import * as fromApp from '../../../store/app.reducers';
import { getCartId, NoopAction } from '../../../store/app.reducers';
import { Action, Store } from '@ngrx/store';
import { environment } from '../../../../environments/environment';
import * as CartActions from '../../../store/cart/cart.actions';
import { PLACE_ORDER_SUCCESS } from '../../../store/cart/cart.actions';
import { Contact } from '../../../model/contact.model';
import { isObjectEmpty, isUndefinedOrNull } from '../../../utils/util-functions';
import { Clear as UserDetailsClear } from '../user-details/user-details.actions';
import { Clear as OrderReferencesClear } from '../order-references/order-references.actions';
import { Clear as DeliveryClear } from '../delivery-details/delivery-details.actions';
import { Clear as BillingClear } from '../billing-details/billing-details.actions';
import { catchError, concatAll, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { LimbusErrorResponse, LimbusErrorStatus, ResponseMessage } from '../../../model/limbus-error-response.model';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { EMPTY, of } from 'rxjs';
import { LocalStorageService } from '@app/core/local-storage/local-storage.service';
import { voucherReferenceKey, VoucherReference } from '@app/order-checkout/store';
import { buildLimbusErrorResponseWithStatus, LimbusErrorWithStatus } from '@app/store/cart/cart.reducers.utils';

@Injectable()
export class OrderCheckoutEffects {


    onValidate = createEffect(() => this.actions$.pipe(
        ofType(VALIDATE),
        withLatestFrom(this.store.select(getCartId)),
        switchMap(([action, cartId]: [Validate, string]) => doValidate(action.payload, cartId, this.httpClient)),
        map((action: Action | Action[]) => {
            if (Array.isArray(action)) {
                return action;
            } else if (action instanceof ValidateFail
      || action instanceof CartActions.PlaceOrderFailure
      || action instanceof CartActions.SetLimbusErrorResponse) {
                return [action];
            }

            return [new ValidateSuccess('')];

        }),
        concatAll()
    ), { dispatch: true });


    onPlaceOrderSuccess = createEffect(() => this.actions$.pipe(
        ofType(PLACE_ORDER_SUCCESS),
        withLatestFrom(this.store.select('contact')),
        switchMap(([action, contacts]) => {

            const contact = !!contacts.loggedInContact ? contacts.loggedInContact : null;

            // if there's contact from login or on behalf of, reset order references
            if (!!contact) {
                return [new OrderReferencesClear()];
            }

            // if there's no contact like for guests, reset user details, order references and address
            return [
                new UserDetailsClear(),
                new OrderReferencesClear(),
                new DeliveryClear(),
                new BillingClear()
            ];
        })
    ));


    onPatch = createEffect(() => this.actions$.pipe(
        ofType(CartActions.UPDATE_CONTACT_DETAILS),
        switchMap((action: CartActions.UpdateContactDetails) =>
            doPatch(action.payload, action.profileId, this.httpClient, this.notification, action.silent))
    ), { dispatch: false });

    // persist voucher reference
    persistVoucherReference$ = createEffect(() => this.actions$.pipe(
        ofType(SET_VOUCHER_REFERENCE),
        // withLatestFrom(this.store.select(getCartId)),
        map(({ reference, id }) => ({ id, reference, dateAdded: (new Date()).getTime() })),
        tap(({ id, reference, dateAdded }) => {
            this.localStorageService.setItem(
                voucherReferenceKey,
                { id, reference, dateAdded });
        })
    ), { dispatch: false }
    );

    // init voucher reference
    initVoucherReference$ = createEffect(() => this.actions$.pipe(
        ofType(INIT_VOUCHER_REFERENCE),
        map(({ id }) => {
            const ref: VoucherReference = this.localStorageService.getItem(voucherReferenceKey);
            if (!!ref && ref.id === id) {
                return new SetVoucherReference(ref.reference, id);
            } else {
                this.localStorageService.removeItem(voucherReferenceKey);
                return new SetVoucherReference('', id);
            }
        })
    ),
    );

    constructor(private actions$: Actions,
        private httpClient: HttpClient,
        private store: Store<fromApp.AppState>,
        private localStorageService: LocalStorageService,
        private notification: NzNotificationService) {
    }
}

export const doPatch = (payload, profileId, httpClient, notification, silent = false) => {
    const contentType = 'application/vnd.hilllabs.contact-update.v1+json';
    const accept = [
        'application/vnd.hilllabs.contact.v1+json',
        HeadersEnum.ACCEPT_ERROR
    ].join(',');

    const url = `${environment.restBaseURL}/contact/${profileId}`;

    const headers = new HttpHeaders({
        'Accept': accept,
        'Content-Type': contentType
    });

    return httpClient.patch(url, getTransformPayload(payload), {
        headers
    }).pipe(
        map((response: Contact) => {
            if (!silent) {
                notification.success('Contact Details', 'Update successful');
            }
        }),
        catchError(err => {
            if (!silent) {
                notification.error('Contact Details', 'Update failed');
            }
            return EMPTY;
        }));
};

// @ts-ignore
export const doValidate = (payload, cartId, httpClient) => {
    return of([new ValidateSuccess('')]); // CP-1207: Skip validation for now since it passes a cart id to a Java endpoint

    const contentType = 'application/vnd.hilllabs.order-header-validation.v1+json';
    const accept = [
        HeadersEnum.ACCEPT_ERROR,
        HeadersEnum.ACCEPT_CART
    ].join(',');
    const params: HttpParams = new HttpParams()
        .set(ParamsKeys.INCLUDE,
            [ParamsValues.CATALOGUE_ITEMS, ParamsValues.PRICES].join(','))
        .set(ParamsKeys.SORT_BY,
            [
                ParamsValues.CART_GROUP_TYPE,
                ParamsValues.CART_GROUP_CREATION_INSTANT,
                ParamsValues.CREATION_INSTANT_DESC,
                ParamsValues.CREATION_GRP_INSTANT_DESC
            ].join(','));

    const url = `${environment.restBaseURL + RestURLsEnum.CART_BASE}${cartId}/validate-order`;

    const headers = new HttpHeaders({
        'Accept': accept,
        'Content-Type': contentType
    });

    return httpClient.post(url, getTransformPayload(payload), {
        headers,
        params
    }).pipe(
        map(r => r),
        catchError(err => [[
            new CartActions.SetLimbusErrorResponse(getLimbusErrorFromHTTPResponse(err)),
            new ValidateFail('')
        ]])
    );
};

export const transformContact = (payload) => {
    const keys = Object.keys(payload);

    return keys.reduce((prev, key) => {

        // special handling of addresses
        if (['deliveryAddress', 'invoiceAddress'].indexOf(key) > -1) {
            return { ...prev, [key]: getTransformPayload(payload[key]) };
        }

        if (!isObjectEmpty(payload[key])) {
            return { ...prev, [key]: { value: payload[key] } };
        }

        return prev;

    }, {});
};

// @ts-ignore
export const getTransformPayload = (payload) => {
    const keys = Object.keys(payload);

    return keys.reduce((prev, key) => {

        // special handling of addresses
        if (['deliveryAddress', 'invoiceAddress'].indexOf(key) > -1) {
            return { ...prev, [key]: getTransformPayload(payload[key]) };
        }

        if (!isObjectEmpty(payload[key])) {
            return { ...prev, [key]: { value: payload[key] } };
        }

        return prev;

    }, {});
};

// @ts-ignore
export const getLimbusErrorFromHTTPResponse = (httpError: HttpErrorResponse): LimbusErrorResponse => {
    const { error }: LimbusErrorWithStatus = buildLimbusErrorResponseWithStatus(
        httpError
    );
    return error;
};
